import React, { useContext, useEffect, useState } from "react";
import { MemberDetails } from "../../Contexts/MemberDetailContext";
import { getApiResponse, postData } from "../../Utils/AxiosRequest";
import {
  API_PATHS,
  errorMessages,
  URL_PATH,
} from "../../Utils/Constants/constants";
import { useForm } from "react-hook-form";
import { Spinner } from "react-bootstrap";
import ConditionalLoader from "../ConditionalLoader";
import ErrorsList from "../ErrorsList";
import {
  checkIsSelected,
  checkNameSpecialCharacters,
  emailValidate,
  phoneValidate,
  pincodeValidate,
} from "../../Utils/validation/validators";
import { useNavigate, useOutletContext } from "react-router-dom";
import { LoginContext } from "../../Contexts/LoginContext";
import SessionTimoutPopup from "../SessionTimoutPopup";
import CenterModal from "../CenterModal";

const PersonalUpdate = () => {
  const { isLoading, memberDetails, setMemberDetails, setIsUpdated } =
    useContext(MemberDetails);
  const { setIsLoggedIn } = useContext(LoginContext);
  const { updateToastMessage } = useOutletContext();
  const [genders, setGenders] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [nationalities, setNationalities] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState({
    heading: "",
    description: "",
  });
  const [file, setFile] = useState("");
  const [apiError, setApiError] = useState("");
  const [sessionTimeout, setSessionTimeout] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
  } = useForm();

  const closePopup = () => {
    setShowModal(false);
    setMessage({
      heading: "",
      description: "",
    });
  };

  const mapDataFromAPI = (key) => {
    if (key === "DOB") {
      setValue(key, memberDetails[key]?.slice(0, 10));
    } else if (memberDetails[key] === "00000000-0000-0000-0000-000000000000") {
      setValue(key, 0);
    } else if (key === "MemberType") {
      setValue(key, memberDetails[key]?.toUpperCase());
    } else {
      setValue(key, memberDetails[key] ?? "");
    }
  };

  const formatReqBody = (data) => {
    const formData = { ...data };
    const reqBody = {};

    Object.keys(formData).forEach((key) => {
      let value = formData[key];
      if (key === "Email" || key === "PhoneNumber") {
        reqBody[key] = memberDetails[key];
      } else if (key === "UploadedFile") {
        const fileArray = Array.from(value);
        reqBody[key] = fileArray[0];
      } else if (key === "UploadedFile") {
        const fileArray = Array.from(value);
        reqBody[key] = fileArray;
      } else if (
        key === "GenderId" ||
        key === "CurrentDesignationId" ||
        key === "NationalityId" ||
        key === "CountryId" ||
        key === "StateId" ||
        key === "DistrictId"
      ) {
        if (value === "0") {
          reqBody[key] = "";
        } else {
          reqBody[key] = value;
        }
      } else if (key === "DOB") {
        const date = new Date(value).toLocaleDateString("en", "dd-mm-yyyy");
        reqBody[key] = date;
      } else if (typeof value === "string" || typeof value === "number") {
        reqBody[key] = value.toString().trim();
      }
    });
    return reqBody;
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    const reqBody = formatReqBody(data);
    reqBody.ModifiedBy = memberDetails.MemberId;
    if (reqBody.UploadedFile?.length) {
      reqBody.DisplayURL = undefined;
      reqBody.ProfileImagePath = undefined;
    }

    try {
      const response = await postData(API_PATHS.updateMember, reqBody, {
        formData: true
      });
      switch (response.status) {
        case 200:
          if (response.data.IsSuccess) {
            updateToastMessage(
              "Personal detail updated successfully.",
              "success"
            );
            navigate(URL_PATH.memberprofile);
            window.scrollTo(0, 0);
            setIsUpdated(true);
            setFile("");
          } else {
            updateToastMessage(errorMessages.error, "danger");
          }
          break;
        case 400:
          updateToastMessage("Validation Error", "danger");
          break;
        case 401:
          setSessionTimeout(true);
          break;
        default:
          updateToastMessage(errorMessages.error, "danger");
      }
    } catch (error) {
      console.error(error);
      updateToastMessage(errorMessages.error, "danger");
    }
    setIsSubmitting(false);
  };

  const handleGetState = async (e) => {
    const CountryId = e.target.value;
    setValue("StateId", "0");
    setValue("DistrictId", "0");

    setStates([]);
    setDistricts([]);

    if (CountryId !== "0") {
      try {
        const res = await getApiResponse(
          `${API_PATHS.getStates}?CountryId=${CountryId}`
        );
        if (res.status === 200) {
          setStates(res.data || []);
        } else {
          console.error("Error: Received unexpected status code", res.status);
          // setStates([]);
        }
      } catch (err) {
        console.error("Error fetching states:", err);
        // setStates([]);
      }
    }
  };

  const handleGetDistrict = async (e) => {
    const StateId = e.target.value;
    setValue("DistrictId", "0");
    setDistricts([]);

    if (StateId !== "0") {
      try {
        const res = await getApiResponse(
          `${API_PATHS.getDisticts}?StateId=${StateId}`
        );
        if (res.status === 200) {
          setDistricts(res.data || []);
        } else {
          console.error("Error: Received unexpected status code", res.status);
          // setDistricts([]);
        }
      } catch (err) {
        console.error("Error fetching districts:", err);
        // setDistricts([]);
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          response,
          genderResponse,
          designationResponse,
          countriesResponse,
        ] = await Promise.all([
          getApiResponse(API_PATHS.getNationalities),
          getApiResponse(API_PATHS.getGenders),
          getApiResponse(API_PATHS.getDesignations),
          getApiResponse(API_PATHS.getCountries),
        ]);
        if (response.status === 200) {
          setNationalities(response.data);
        }
        if (genderResponse.status === 200) {
          setGenders(genderResponse.data ?? []);
        }
        if (designationResponse.status === 200) {
          setDesignations(designationResponse.data ?? []);
        }
        if (countriesResponse.status === 200) {
          setCountries(countriesResponse.data ?? []);
        }
      } catch (error) {
        console.error("Error", error);
        setApiError("Something went wrong please try refreshing.");
      }
    };
    const fetchMemberDetails = async () => {
      const response = await getApiResponse(API_PATHS.viewMember);
      if (response.status === 200) {
        const memberDetailResponse = response?.data?.MemberDetails ?? {};

        // Load states for the member's country if it exists
        if (memberDetailResponse.CountryId) {
          const statesRes = await getApiResponse(
            `${API_PATHS.getStates}?CountryId=${memberDetailResponse.CountryId}`
          );
          if (statesRes.status === 200) {
            setStates(statesRes.data || []);

            // Load districts for the member's state if it exists
            if (memberDetailResponse.StateId) {
              const districtsRes = await getApiResponse(
                `${API_PATHS.getDisticts}?StateId=${memberDetailResponse.StateId}`
              );
              if (districtsRes.status === 200) {
                setDistricts(districtsRes.data || []);
              }
            }
          }
        }
        Object.keys(memberDetailResponse).forEach(mapDataFromAPI);
      }
    };
    const loadData = async () => {
      setIsDataLoading(true);
      await fetchData();
      await fetchMemberDetails();
      setIsDataLoading(false);
    }
    loadData()
  }, []);

  useEffect(() => {
  }, []);

  return (
    <ConditionalLoader isLoading={isDataLoading || isLoading}>
      <SessionTimoutPopup isSessionTimout={sessionTimeout}>
        <div className="tab-pane active mb-2">
          {apiError && <p className="text-white p-2 w-full ">{apiError}</p>}
          <h4 className="sub-title mb-6">Personal Detail</h4>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-sm-12 row pb-2">
                <div className="col-sm-4 col-md-3">
                  {memberDetails.DisplayURL || file ? (
                    <div className="profile-img">
                      <img
                        src={file ? file : memberDetails?.DisplayURL}
                        alt=""
                      />
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
              <div
                className="col-sm-6"
                style={{
                  zIndex: 0,
                }}
              >
                <div className="form-group">
                  <div className="custom-file">
                    <input
                      type="file"
                      className="custom-file-input"
                      accept="image/*"
                      id="customFile"
                      {...register("UploadedFile", {
                        onChange: (e) => {
                          setFile(URL.createObjectURL(e.target.files[0]));
                        },
                      })}
                      disabled={isSubmitting}
                    />
                    <label className="custom-file-label" htmlFor="customFile">
                      Upload Your Image
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <input
                    type="email"
                    className={`form-control ${errors.Email && isDirty
                      ? "red-placeholder border border-danger"
                      : ""
                      }`}
                    placeholder={
                      errors.Email && isDirty ? errors.Email.message : "Email"
                    }
                    autoComplete="off"
                    {...register("Email", {
                      required: "Email is required",
                      validate: emailValidate,
                    })}
                    disabled={true}
                  />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <input
                    type="text"
                    className={`form-control ${errors.PhoneNumber && isDirty
                      ? "red-placeholder border border-danger"
                      : ""
                      }`}
                    placeholder={
                      errors.PhoneNumber && isDirty
                        ? errors.PhoneNumber.message
                        : "Phone No."
                    }
                    onInput={(e) => {
                      const regex = /[^0-9+]/g;
                      e.target.value = e.target.value.replace(regex, "");
                    }}
                    {...register("PhoneNumber", {
                      required: "Please enter your phone number",
                      maxLength: {
                        value: 15,
                        message: "Please enter a valid mobile number",
                      },
                      minLength: {
                        value: 10,
                        message: "Please enter a valid mobile number",
                      },
                      validate: phoneValidate,
                    })}
                    disabled={true}
                  />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <input
                    type="text"
                    className={`form-control ${errors.FirstName && isDirty
                      ? "red-placeholder border border-danger"
                      : ""
                      }`}
                    placeholder={
                      errors.FirstName && isDirty
                        ? errors.FirstName.message
                        : "*First Name"
                    }
                    autoComplete="off"
                    {...register("FirstName", {
                      required: "First Name is required",
                      setValueAs: (value) => value.trim() || "",
                      validate: (v) =>
                        checkNameSpecialCharacters(v, "First Name"),
                    })}
                    disabled={isSubmitting}
                  />
                  {errors.FirstName && (
                    <p className="text-danger text-left p-0 m-0">
                      <small>{`* ${errors.FirstName.message}`}</small>
                    </p>
                  )}
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <input
                    type="text"
                    className={`form-control ${errors.LastName && isDirty
                      ? "red-placeholder border border-danger"
                      : ""
                      }`}
                    placeholder={
                      errors.LastName && isDirty
                        ? errors.LastName.message
                        : "*Last Name"
                    }
                    autoComplete="off"
                    {...register("LastName", {
                      required: "Last Name is required",
                      setValueAs: (value) => value.trim() || "",
                      validate: (v) =>
                        checkNameSpecialCharacters(v, "Last Name"),
                    })}
                    disabled={isSubmitting}
                  />
                  {errors.LastName && (
                    <p className="text-danger text-left p-0 m-0">
                      <small>{`* ${errors.LastName.message}`}</small>
                    </p>
                  )}
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <input
                    type="text"
                    className={`form-control ${errors.FatherName && isDirty
                      ? "red-placeholder border border-danger"
                      : ""
                      }`}
                    placeholder={
                      errors.FatherName && isDirty
                        ? errors.FatherName.message
                        : "*Father’s Name"
                    }
                    autoComplete="off"
                    {...register("FatherName", {
                      required: "Father's Name is required",
                      setValueAs: (value) => value.trim() || "",
                    })}
                    disabled={isSubmitting}
                  />
                  {errors.FatherName && (
                    <p className="text-danger text-left p-0 m-0">
                      <small>{`* ${errors.FatherName.message}`}</small>
                    </p>
                  )}
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <select
                    className={`custom-select ${errors.GenderId && isDirty
                      ? "red-placeholder border border-danger"
                      : ""
                      }`}
                    {...register("GenderId", {
                      validate: (v) => checkIsSelected(v, "Gender"),
                    })}
                    disabled={isSubmitting}
                  >
                    <option key={0} value={0} disabled>
                      *Gender
                    </option>
                    {genders.length > 0 &&
                      genders.map((item) => (
                        <option key={item.GenderId} value={item.GenderId}>
                          {item.GenderDesc.toUpperCase()}
                        </option>
                      ))}
                  </select>
                  {errors.GenderId && (
                    <p className="text-danger text-left p-0 m-0">
                      <small>{`* ${errors.GenderId.message}`}</small>
                    </p>
                  )}
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <input
                    type="text"
                    className={`form-control ${errors.DOB && isDirty
                      ? "red-placeholder border border-danger"
                      : ""
                      }`}
                    placeholder={
                      errors.DOB && isDirty ? errors.DOB.message : "*DOB"
                    }
                    {...register("DOB", {
                      required: "Please add your Date of Birth",
                      validate: {
                        validYear: (value) => {
                          if (!value) return true;
                          const selectedDate = new Date(value);
                          const selectedYear = selectedDate.getFullYear();
                          const currentYear = new Date().getFullYear();
                          return (
                            (selectedYear >= 1900 &&
                              selectedYear <= currentYear) ||
                            "Year must be between 1900 and current year"
                          );
                        },
                        notFuture: (value) => {
                          if (!value) return true;
                          const selectedDate = new Date(value);
                          const today = new Date();
                          return (
                            selectedDate <= today ||
                            "Date cannot be in the future"
                          );
                        },
                      },
                    })}
                    onFocus={(e) => {
                      e.target.type = "date";
                    }}
                    onBlur={(e) => {
                      e.target.type = "text";
                    }}
                    disabled={isSubmitting}
                  />
                  {errors.DOB && (
                    <p className="text-danger text-left p-0 m-0">
                      <small>{`* ${errors.DOB.message}`}</small>
                    </p>
                  )}
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <select
                    className={`custom-select ${errors.CurrentDesignationId && isDirty
                      ? "red-placeholder border border-danger"
                      : ""
                      }`}
                    placeholder={
                      errors.CurrentDesignationId && isDirty
                        ? errors.CurrentDesignationId.message
                        : "*Designation"
                    }
                    {...register("CurrentDesignationId", {
                      validate: (v) => checkIsSelected(v, "Designation"),
                      required: "Please select Designation",
                    })}
                    disabled={isSubmitting}
                  >
                    <option key={0} value={0} disabled>
                      *Designation
                    </option>
                    {designations.length > 0 &&
                      designations.map((item) => (
                        <option
                          key={item.DesignationId}
                          value={item.DesignationId}
                        >
                          {item.DesignationDesc.toUpperCase()}
                        </option>
                      ))}
                  </select>
                  {errors.CurrentDesignationId && (
                    <p className="text-danger text-left p-0 m-0">
                      <small>{`* ${errors.CurrentDesignationId.message}`}</small>
                    </p>
                  )}
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <select
                    className={`custom-select ${errors.NationalityId && isDirty
                      ? "red-placeholder border border-danger"
                      : ""
                      }`}
                    {...register("NationalityId", {
                      validate: (v) => checkIsSelected(v, "Nationality"),
                    })}
                    disabled={isSubmitting}
                  >
                    <option key={0} value={0} disabled>
                      *Nationality
                    </option>
                    {nationalities.length > 0 &&
                      nationalities.map((item) => (
                        <option
                          key={item.NationalityId}
                          value={item.NationalityId}
                        >
                          {item.Country.toUpperCase()}
                        </option>
                      ))}
                  </select>
                  {errors.NationalityId && (
                    <p className="text-danger text-left p-0 m-0">
                      <small>{`* ${errors.NationalityId.message}`}</small>
                    </p>
                  )}
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <select
                    className={`custom-select ${errors.MemberType && isDirty
                      ? "red-placeholder border border-danger"
                      : ""
                      }`}
                    {...register("MemberType")}
                    disabled={isSubmitting}
                  >
                    <option value="A">Active Member</option>
                    <option value="V">Volunteer</option>
                  </select>
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <input
                    type="text"
                    className={`form-control ${errors.AddressLine1 && isDirty
                      ? "red-placeholder border border-danger"
                      : ""
                      }`}
                    placeholder={
                      errors.AddressLine1 && isDirty
                        ? errors.AddressLine1.message
                        : "*Permanent Address Line-1"
                    }
                    {...register("AddressLine1", {
                      required: "Please enter Address",
                    })}
                    disabled={isSubmitting}
                  />
                  {errors.AddressLine1 && (
                    <p className="text-danger text-left p-0 m-0">
                      <small>{`* ${errors.AddressLine1.message}`}</small>
                    </p>
                  )}
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <input
                    type="text"
                    className={`form-control ${errors.AddressLine2 && isDirty
                      ? "red-placeholder border border-danger"
                      : ""
                      }`}
                    placeholder={
                      errors.AddressLine2 && isDirty
                        ? errors.AddressLine2.message
                        : "Permanent Address Line-2"
                    }
                    {...register("AddressLine2")}
                    disabled={isSubmitting}
                  />
                  {errors.AddressLine2 && (
                    <p className="text-danger text-left p-0 m-0">
                      <small>{`* ${errors.AddressLine2.message}`}</small>
                    </p>
                  )}
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <input
                    type="text"
                    className={`form-control ${errors.CityName && isDirty
                      ? "red-placeholder border border-danger"
                      : ""
                      }`}
                    placeholder={
                      errors.CityName && isDirty
                        ? errors.CityName.message
                        : "*City"
                    }
                    {...register("CityName", {
                      required: "Please enter your city",
                      setValueAs: (value) => value.trim() || "",
                    })}
                    disabled={isSubmitting}
                  />
                  {errors.CityName && (
                    <p className="text-danger text-left p-0 m-0">
                      <small>{`* ${errors.CityName.message}`}</small>
                    </p>
                  )}
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <select
                    className={`custom-select ${errors.CountryId && isDirty
                      ? "red-placeholder border border-danger"
                      : ""
                      }`}
                    {...register("CountryId", {
                      validate: (v) => checkIsSelected(v, "Contry"),
                      onChange: handleGetState,
                    })}
                    disabled={isSubmitting}
                  >
                    <option key={0} value={0} disabled>
                      *Country
                    </option>
                    {countries.length > 0 &&
                      countries.map((item) => (
                        <option key={item.CountryId} value={item.CountryId}>
                          {item.Name.toUpperCase()}
                        </option>
                      ))}
                  </select>
                  {errors.CountryId && (
                    <p className="text-danger text-left p-0 m-0">
                      <small>{`* ${errors.CountryId.message}`}</small>
                    </p>
                  )}
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <select
                    className={`custom-select ${errors.StateId && isDirty
                      ? "red-placeholder border border-danger"
                      : ""
                      }`}
                    defaultValue={0}
                    {...register("StateId", {
                      validate: (v) => checkIsSelected(v, "State"),
                      onChange: handleGetDistrict,
                    })}
                    disabled={isSubmitting}
                  >
                    <option key={0} value={0} disabled>
                      *State
                    </option>
                    {states.length > 0 &&
                      states.map((item) => (
                        <option key={item.StateId} value={item.StateId}>
                          {item.Name.toUpperCase()}
                        </option>
                      ))}
                  </select>
                  {errors.StateId && (
                    <p className="text-danger text-left p-0 m-0">
                      <small>{`* ${errors.StateId.message}`}</small>
                    </p>
                  )}
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <select
                    className={`custom-select ${errors.DistrictId && isDirty
                      ? "red-placeholder border border-danger"
                      : ""
                      }`}
                    defaultValue={0}
                    {...register("DistrictId", {
                      validate: (v) => checkIsSelected(v, "District"),
                    })}
                    disabled={isSubmitting}
                  >
                    <option key={0} value={0} disabled>
                      *District
                    </option>
                    {districts.length > 0 &&
                      districts.map((item) => (
                        <option key={item.CityId} value={item.CityId}>
                          {item.Name.toUpperCase()}
                        </option>
                      ))}
                  </select>
                  {errors.DistrictId && (
                    <p className="text-danger text-left p-0 m-0">
                      <small>{`* ${errors.DistrictId.message}`}</small>
                    </p>
                  )}
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <input
                    type="text"
                    className={`form-control ${errors.Pincode && isDirty
                      ? "red-placeholder border border-danger"
                      : ""
                      }`}
                    placeholder={
                      errors.Pincode && isDirty
                        ? errors.Pincode.message
                        : "*Pincode"
                    }
                    {...register("Pincode", {
                      validate: pincodeValidate,
                    })}
                    disabled={isSubmitting}
                  />
                  {errors.Pincode && (
                    <p className="text-danger text-left p-0 m-0">
                      <small>{`* ${errors.Pincode.message}`}</small>
                    </p>
                  )}
                </div>
              </div>

              <div className="col-sm-6">
                <div className="form-group">
                  <input
                    type="text"
                    className={`form-control ${errors.RecommendedBy && isDirty
                      ? "red-placeholder border border-danger"
                      : ""
                      }`}
                    placeholder={
                      errors.RecommendedBy && isDirty
                        ? errors.RecommendedBy.message
                        : "Recommended By"
                    }
                    {...register("RecommendedBy")}
                    disabled={isSubmitting}
                  />
                  {errors.RecommendedBy && (
                    <p className="text-danger text-left p-0 m-0">
                      <small>{`* ${errors.RecommendedBy.message}`}</small>
                    </p>
                  )}
                </div>
              </div>
              {/* <ErrorsList isDirty={isDirty} errors={errors} /> */}
              <ul className="col-md-12 list-inline text-right d-flex justify-content-end">
                <li>
                  {isSubmitting ? (
                    <button className="btn green-btn px-5 cursor-na" disabled>
                      <Spinner size="sm" />
                    </button>
                  ) : (
                    <input
                      type="submit"
                      className="btn green-btn"
                      disabled={
                        isSubmitting ||
                        !isDirty ||
                        Object.keys(errors).length > 0
                      }
                      value="Save"
                    />
                  )}
                </li>
              </ul>
            </div>
          </form>
          <CenterModal
            show={showModal}
            onClose={closePopup}
            heading={message.heading}
            description={message.description}
            buttonText={"Ok"}
          />
        </div>
      </SessionTimoutPopup>
    </ConditionalLoader>
  );
};

export default PersonalUpdate;
